import type { SliceComponentProps } from "@prismicio/react";
import { PrismicImage, PrismicRichText } from "@prismicio/react";
import { asText, asLink } from "@prismicio/helpers";
import type { AuthorBioSectionSlice } from "prismicio-types";
import { linkResolver } from "~/lib/prismic/utils";
import { useLocalePath } from "@commercekit/remix";
import WvLink from "~/components/atoms/WvLink";

const DEFAULTS = {
  authorName: "Jesse Keizer",
  authorImageUrl:
    "https://images.prismic.io/wovar-rb2-dev/ZyDUhK8jQArTz9XP_Jesse.jpg?auto=format%2Ccompress&width=240",
  authorImageAlt: "Jesse Wovar doosjes Loods",
  linkedinUrl: "https://nl.linkedin.com/in/jesse-keizer-a6014854"
};

const AuthorBioSection = ({
  slice,
}: SliceComponentProps<AuthorBioSectionSlice>) => {
  const localePath = useLocalePath();

  const preHeadline = slice.primary.pre_headline || "";
  const authorName = slice.primary.author_name || DEFAULTS.authorName;
  const linkedinUrl =
    asLink(slice.primary.linkedin_url, linkResolver) ||
    (slice.primary.linkedin_url as { url?: string } | null)?.url ||
    DEFAULTS.linkedinUrl;
  const ctaLink = asLink(slice.primary.cta_link, linkResolver);
  const ctaText =
    (slice.primary.cta_link as { text?: string })?.text || "";
  const ctaHref = ctaLink || localePath("/over-wovar");
  const hasBio =
    slice.primary.bio && slice.primary.bio.length > 0 && asText(slice.primary.bio);
  const hasImage = !!slice.primary.author_image?.url;

  return (
    <section
      className="col-12 author-bio-section mb-3 mb-lg-5"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <div className="author-bio-section__card">
        <div className="author-bio-section__left">
          <div className="author-bio-section__image-wrapper">
            {hasImage ? (
              <PrismicImage
                field={slice.primary.author_image}
                className="author-bio-section__photo"
                loading="lazy"
              />
            ) : (
              <img
                src={DEFAULTS.authorImageUrl}
                alt={DEFAULTS.authorImageAlt}
                className="author-bio-section__photo"
                loading="lazy"
              />
            )}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 100 100"
              className="author-bio-section__hexagon-overlay"
              aria-hidden="true"
            >
              <path
                fill="#E8B001"
                fillRule="evenodd"
                d="M100 50 L75 93.3 L25 93.3 L0 50 L25 6.7 L75 6.7 Z M50 14 A36 36 0 1 0 50 86 A36 36 0 1 0 50 14 Z"
              />
            </svg>
          </div>
          {linkedinUrl && (
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="author-bio-section__linkedin"
              aria-label="LinkedIn"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="#0A66C2"
              >
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          )}
        </div>

        <div className="author-bio-section__content">
          <h3 className="author-bio-section__title">
            {preHeadline} <strong>{authorName}</strong>
          </h3>

          {hasBio && (
            <div className="author-bio-section__bio">
              <PrismicRichText field={slice.primary.bio} />
            </div>
          )}

          <WvLink to={ctaHref} className="btn btn--primary author-bio-section__cta">
            {ctaText}
          </WvLink>
        </div>
      </div>
    </section>
  );
};

export default AuthorBioSection;
